import React, { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../../../components/ui/dialog";
import {
	BookOpen,
	ChevronDown,
	ChevronRight,
	Globe,
	Settings,
	Zap,
	Code,
	Key,
	Link,
	MapPin,
	ArrowRight,
	Phone,
	Send,
	Search,
	ShoppingCart,
	AlertTriangle,
	CheckCircle,
	Copy,
	Check,
	X
} from "lucide-react";

const CodeBlock = ({ children, language = "json" }) => {
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		navigator.clipboard.writeText(children);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<div className="relative group my-2">
			<pre className="bg-black/40 border border-amber-500/20 rounded-lg p-3 overflow-x-auto text-xs text-amber-200/90 font-mono">
				<code>{children}</code>
			</pre>
			<button
				onClick={handleCopy}
				className="absolute top-2 right-2 p-1.5 rounded-md bg-amber-500/20 text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-amber-500/30"
				title="Copiar"
			>
				{copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
			</button>
		</div>
	);
};

const AccordionItem = ({ icon: Icon, title, children, defaultOpen = false }) => {
	const [isOpen, setIsOpen] = useState(defaultOpen);

	return (
		<div className="border border-amber-500/20 rounded-lg overflow-hidden mb-3">
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="w-full flex items-center gap-3 p-3 bg-amber-500/5 hover:bg-amber-500/10 transition-colors text-left"
			>
				{Icon && <Icon className="w-4 h-4 text-amber-400 flex-shrink-0" />}
				<span className="text-sm font-medium text-textPrimary flex-1">{title}</span>
				{isOpen ? (
					<ChevronDown className="w-4 h-4 text-amber-400" />
				) : (
					<ChevronRight className="w-4 h-4 text-amber-400" />
				)}
			</button>
			<div className={`transition-all duration-300 ${isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}>
				<div className="p-3 pt-2 text-sm text-textSecondary leading-relaxed">
					{children}
				</div>
			</div>
		</div>
	);
};

const StepNumber = ({ n }) => (
	<span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500/30 text-amber-300 text-xs font-bold mr-2 flex-shrink-0">
		{n}
	</span>
);

const Tag = ({ children, color = "amber" }) => (
	<span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${color}-500/20 text-${color}-400 border border-${color}-500/30`}>
		{children}
	</span>
);

export const APIFunctionsHelpDialog = ({ isOpen, onClose }) => {

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<BookOpen className="w-5 h-5 text-amber-400" />
						<span>Documentação — Ferramentas de API</span>
					</DialogTitle>
				</DialogHeader>

				<div className="flex-1 min-h-0">
					<div className="overflow-y-auto pr-1 space-y-6 scroll-smooth max-h-[calc(85vh-140px)]" style={{ scrollbarWidth: "thin" }}>

						{/* ─── INTRO ─── */}
						<section id="help-section-intro">
							<h3 className="text-base font-semibold text-amber-400 mb-2 flex items-center gap-2">
								<Zap className="w-4 h-4" />
								O que são Ferramentas de API?
							</h3>
							<p className="text-sm text-textSecondary leading-relaxed mb-3">
								São APIs externas que a sua IA pode chamar automaticamente durante conversas no WhatsApp.
								Quando um cliente faz uma pergunta que requer dados externos (CEP, preço, estoque, etc.),
								a IA identifica a necessidade, chama a API e responde com os dados reais.
							</p>
							<div className="bg-amber-500/10 border border-amber-500/25 rounded-lg p-3 text-sm">
								<p className="text-amber-200 font-medium mb-2">💡 Exemplo prático:</p>
								<div className="space-y-1 text-amber-200/80 text-xs">
									<p><strong>Cliente:</strong> "Qual o endereço do CEP 01310-100?"</p>
									<p><strong>IA:</strong> <em>(chama API do ViaCEP automaticamente)</em></p>
									<p><strong>IA responde:</strong> "O CEP 01310-100 corresponde à Avenida Paulista, São Paulo - SP"</p>
								</div>
							</div>
						</section>

						{/* ─── GET EXAMPLE ─── */}
						<section id="help-section-get">
							<h3 className="text-base font-semibold text-amber-400 mb-2 flex items-center gap-2">
								<Search className="w-4 h-4" />
								Exemplo GET — Consultar CEP
							</h3>
							<p className="text-sm text-textSecondary mb-3">
								O método <strong className="text-amber-300">GET</strong> é usado para <strong>buscar informações</strong>. É o mais comum.
							</p>

							<div className="space-y-3">
								<div className="flex items-start gap-2">
									<StepNumber n={1} />
									<div className="flex-1">
										<p className="text-sm text-textPrimary font-medium">Defina a URL com o parâmetro no path</p>
										<CodeBlock>{`https://viacep.com.br/ws/{cep}/json/`}</CodeBlock>
										<p className="text-xs text-grayText">Use <code className="text-amber-300">{`{cep}`}</code> onde o valor será substituído.</p>
									</div>
								</div>

								<div className="flex items-start gap-2">
									<StepNumber n={2} />
									<div className="flex-1">
										<p className="text-sm text-textPrimary font-medium">Adicione o parâmetro</p>
										<div className="bg-black/30 rounded-lg p-3 mt-1 space-y-1 text-xs">
											<p><span className="text-grayText">Nome:</span> <span className="text-amber-300">cep</span></p>
											<p><span className="text-grayText">Localização:</span> <span className="text-amber-300">Path - substitui {`{param}`} na URL</span></p>
											<p><span className="text-grayText">Obrigatório:</span> <span className="text-green-400">✓ Sim</span></p>
										</div>
									</div>
								</div>

								<div className="flex items-start gap-2">
									<StepNumber n={3} />
									<div className="flex-1">
										<p className="text-sm text-textPrimary font-medium">Preencha nome e descrição da função</p>
										<div className="bg-black/30 rounded-lg p-3 mt-1 space-y-1 text-xs">
											<p><span className="text-grayText">Nome:</span> <span className="text-amber-300">consultar_cep</span></p>
											<p><span className="text-grayText">Descrição:</span> <span className="text-amber-300">Consulta endereço pelo CEP brasileiro</span></p>
										</div>
										<p className="text-xs text-grayText mt-1">A descrição ajuda a IA a entender <strong>quando</strong> usar esta função.</p>
									</div>
								</div>

								<div className="flex items-start gap-2">
									<StepNumber n={4} />
									<div className="flex-1">
										<p className="text-sm text-textPrimary font-medium">Clique em "Criar Função" e teste!</p>
										<p className="text-xs text-grayText">Use o botão de teste para verificar se a API responde corretamente.</p>
									</div>
								</div>
							</div>

							<div className="mt-3 p-3 bg-green-500/10 border border-green-500/25 rounded-lg text-xs">
								<p className="text-green-400 font-medium mb-1">✅ Resultado</p>
								<p className="text-green-300/80">Quando o cliente perguntar sobre um CEP, a IA chamará automaticamente:</p>
								<CodeBlock>{`GET https://viacep.com.br/ws/01310100/json/`}</CodeBlock>
							</div>
						</section>

						{/* ─── POST EXAMPLE ─── */}
						<section id="help-section-post">
							<h3 className="text-base font-semibold text-amber-400 mb-2 flex items-center gap-2">
								<Send className="w-4 h-4" />
								Exemplo POST — Enviar dados
							</h3>
							<p className="text-sm text-textSecondary mb-3">
								O método <strong className="text-amber-300">POST</strong> é usado para <strong>enviar dados</strong> a uma API (cadastrar, criar, registrar).
							</p>

							<div className="space-y-3">
								<div className="flex items-start gap-2">
									<StepNumber n={1} />
									<div className="flex-1">
										<p className="text-sm text-textPrimary font-medium">Selecione o método POST e defina a URL</p>
										<CodeBlock>{`https://api.exemplo.com/contatos`}</CodeBlock>
									</div>
								</div>

								<div className="flex items-start gap-2">
									<StepNumber n={2} />
									<div className="flex-1">
										<p className="text-sm text-textPrimary font-medium">Adicione parâmetros com localização "Body"</p>
										<p className="text-xs text-grayText mb-2">A opção "Body" só aparece quando o método é POST ou PUT.</p>
										<div className="bg-black/30 rounded-lg p-3 mt-1 text-xs space-y-2">
											<div className="flex items-center gap-3">
												<span className="text-amber-300 font-medium w-24">name</span>
												<span className="text-grayText">→ Body</span>
												<span className="text-green-400 text-xs">obrigatório</span>
											</div>
											<div className="flex items-center gap-3">
												<span className="text-amber-300 font-medium w-24">email</span>
												<span className="text-grayText">→ Body</span>
												<span className="text-green-400 text-xs">obrigatório</span>
											</div>
											<div className="flex items-center gap-3">
												<span className="text-amber-300 font-medium w-24">whatsapp</span>
												<span className="text-grayText">→ Body</span>
												<span className="text-amber-400 text-xs">auto-preenchido ✨</span>
											</div>
										</div>
									</div>
								</div>

								<div className="flex items-start gap-2">
									<StepNumber n={3} />
									<div className="flex-1">
										<p className="text-sm text-textPrimary font-medium">Resultado: JSON enviado no corpo da requisição</p>
										<CodeBlock>{`POST https://api.exemplo.com/contatos

{
  "name": "João Silva",
  "email": "joao@email.com",
  "whatsapp": "+5511999999999"
}`}</CodeBlock>
										<p className="text-xs text-grayText">O campo <code className="text-amber-300">whatsapp</code> é preenchido automaticamente com o número do cliente!</p>
									</div>
								</div>
							</div>
						</section>

						{/* ─── PARAM TYPES ─── */}
						<section id="help-section-params">
							<h3 className="text-base font-semibold text-amber-400 mb-2 flex items-center gap-2">
								<Settings className="w-4 h-4" />
								Tipos de Parâmetros
							</h3>

							<div className="space-y-2">
								<AccordionItem icon={MapPin} title='Path — substitui {param} na URL' defaultOpen>
									<p className="mb-2">O valor substitui o placeholder <code className="text-amber-300">{`{param}`}</code> diretamente na URL.</p>
									<div className="bg-black/30 rounded-lg p-2 text-xs mb-2 space-y-1">
										<p className="text-grayText">URL configurada:</p>
										<p className="text-amber-300 font-mono">https://api.com/users/<strong>{`{id}`}</strong>/pedidos</p>
										<p className="text-grayText mt-1">Resultado com id = 42:</p>
										<p className="text-green-300 font-mono">https://api.com/users/<strong>42</strong>/pedidos</p>
									</div>
									<p className="text-xs text-grayText">Ideal para: IDs, CEPs, CNPJs, códigos.</p>
								</AccordionItem>

								<AccordionItem icon={Link} title="Query — adiciona ?param=valor">
									<p className="mb-2">O valor é enviado como parâmetro de URL (query string).</p>
									<div className="bg-black/30 rounded-lg p-2 text-xs mb-2 space-y-1">
										<p className="text-grayText">URL configurada:</p>
										<p className="text-amber-300 font-mono">https://api.com/buscar</p>
										<p className="text-grayText mt-1">Resultado com termo = "notebook":</p>
										<p className="text-green-300 font-mono">https://api.com/buscar<strong>?termo=notebook</strong></p>
									</div>
									<p className="text-xs text-grayText">Ideal para: buscas, filtros, parâmetros opcionais.</p>
								</AccordionItem>

								<AccordionItem icon={Code} title="Body — envia no corpo JSON (POST/PUT)">
									<p className="mb-2">O valor é enviado no corpo da requisição como JSON. <strong>Disponível apenas para POST e PUT.</strong></p>
									<div className="bg-black/30 rounded-lg p-2 text-xs mb-2 space-y-1">
										<p className="text-grayText">Parâmetros: name, email, phone</p>
										<p className="text-grayText mt-1">Corpo enviado:</p>
										<CodeBlock>{`{
  "name": "Maria",
  "email": "maria@email.com",
  "phone": "+5511888887777"
}`}</CodeBlock>
									</div>
									<p className="text-xs text-grayText">Ideal para: cadastros, criação de registros, envio de formulários.</p>
								</AccordionItem>
							</div>

							<div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/25 rounded-lg text-xs">
								<p className="text-blue-300 font-medium mb-1">💡 Dica: você pode combinar tipos!</p>
								<p className="text-blue-300/80">Uma mesma função pode ter parâmetros Path + Query + Body ao mesmo tempo.</p>
								<p className="text-blue-300/70 mt-1">Ex: <code className="text-blue-200">POST https://api.com/lojas/{`{loja_id}`}/pedidos?notify=true</code> com dados no body.</p>
							</div>
						</section>

						{/* ─── HEADERS ─── */}
						<section id="help-section-headers">
							<h3 className="text-base font-semibold text-amber-400 mb-2 flex items-center gap-2">
								<Key className="w-4 h-4" />
								Headers e Autenticação
							</h3>
							<p className="text-sm text-textSecondary mb-3">
								Muitas APIs exigem autenticação. Você pode configurar headers personalizados em cada função.
							</p>

							<AccordionItem icon={Key} title="Bearer Token (mais comum)" defaultOpen>
								<div className="bg-black/30 rounded-lg p-2 text-xs space-y-1">
									<p><span className="text-grayText">Nome:</span> <span className="text-amber-300">Authorization</span></p>
									<p><span className="text-grayText">Valor:</span> <span className="text-amber-300">Bearer seu_token_aqui</span></p>
								</div>
							</AccordionItem>

							<AccordionItem icon={Key} title="API Key">
								<div className="bg-black/30 rounded-lg p-2 text-xs space-y-1">
									<p><span className="text-grayText">Nome:</span> <span className="text-amber-300">X-API-Key</span></p>
									<p><span className="text-grayText">Valor:</span> <span className="text-amber-300">sua_chave_aqui</span></p>
								</div>
								<p className="text-xs text-grayText mt-2">O nome do header varia conforme a documentação de cada API.</p>
							</AccordionItem>

							<AccordionItem icon={Key} title="Basic Auth">
								<div className="bg-black/30 rounded-lg p-2 text-xs space-y-1">
									<p><span className="text-grayText">Nome:</span> <span className="text-amber-300">Authorization</span></p>
									<p><span className="text-grayText">Valor:</span> <span className="text-amber-300">Basic dXN1YXJpbzpzZW5oYQ==</span></p>
								</div>
								<p className="text-xs text-grayText mt-2">O valor é <code className="text-amber-300">Basic</code> seguido de <code className="text-amber-300">usuario:senha</code> em Base64.</p>
							</AccordionItem>

							<div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/25 rounded-lg text-xs">
								<p className="text-amber-300">
									<strong>Nota:</strong> Os headers <code className="text-amber-200">Content-Type: application/json</code> e <code className="text-amber-200">User-Agent</code> já são enviados automaticamente.
								</p>
							</div>
						</section>

						{/* ─── WHATSAPP AUTO ─── */}
						<section id="help-section-whatsapp">
							<h3 className="text-base font-semibold text-amber-400 mb-2 flex items-center gap-2">
								<Phone className="w-4 h-4" />
								Número do WhatsApp Automático
							</h3>
							<p className="text-sm text-textSecondary mb-3">
								O número do WhatsApp do cliente que está conversando é <strong className="text-amber-300">injetado automaticamente</strong> em parâmetros
								com nomes relacionados a telefone.
							</p>

							<div className="bg-green-500/10 border border-green-500/25 rounded-lg p-3 mb-3">
								<p className="text-green-400 text-sm font-medium mb-2">✨ Nomes reconhecidos automaticamente:</p>
								<div className="flex flex-wrap gap-2">
									{["whatsapp", "phone", "telefone", "celular", "numero", "mobile"].map(name => (
										<span key={name} className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-xs font-mono border border-green-500/30">
											{name}
										</span>
									))}
								</div>
								<p className="text-green-300/70 text-xs mt-2">Qualquer parâmetro que contenha uma dessas palavras no nome será auto-preenchido.</p>
							</div>

							<p className="text-sm text-textSecondary mb-2">
								<strong>Exemplos</strong> de nomes que funcionam:
							</p>
							<div className="bg-black/30 rounded-lg p-3 text-xs space-y-1 mb-3">
								<p><span className="text-green-400">✓</span> <code className="text-amber-300">whatsapp</code> → <span className="text-grayText">+5511999999999</span></p>
								<p><span className="text-green-400">✓</span> <code className="text-amber-300">phone_number</code> → <span className="text-grayText">+5511999999999</span></p>
								<p><span className="text-green-400">✓</span> <code className="text-amber-300">numero_celular</code> → <span className="text-grayText">+5511999999999</span></p>
								<p><span className="text-green-400">✓</span> <code className="text-amber-300">telefone_cliente</code> → <span className="text-grayText">+5511999999999</span></p>
								<p><span className="text-red-400">✗</span> <code className="text-grayText">nome</code> → <span className="text-grayText">não é reconhecido</span></p>
							</div>

							<div className="p-3 bg-blue-500/10 border border-blue-500/25 rounded-lg text-xs">
								<p className="text-blue-300 font-medium mb-1">💡 Prioridade: IA primeiro</p>
								<p className="text-blue-300/80">Se a IA já preencheu o valor (ex: o cliente disse o número na conversa), o valor da IA é mantido. A auto-injeção só preenche campos <strong>vazios</strong>.</p>
							</div>
						</section>

						{/* ─── TIPS ─── */}
						<section id="help-section-tips">
							<h3 className="text-base font-semibold text-amber-400 mb-2 flex items-center gap-2">
								<CheckCircle className="w-4 h-4" />
								Dicas e Boas Práticas
							</h3>

							<div className="space-y-3 text-sm text-textSecondary">
								<div className="flex items-start gap-2">
									<span className="text-amber-400 mt-0.5">1.</span>
									<div>
										<p className="text-textPrimary font-medium">Use descrições claras</p>
										<p className="text-xs text-grayText">A descrição da função é o que a IA usa para decidir quando chamá-la. Seja específico.</p>
										<div className="mt-1 text-xs">
											<p><span className="text-red-400">✗</span> <span className="text-grayText">"Consulta CEP"</span></p>
											<p><span className="text-green-400">✓</span> <span className="text-amber-300">"Consulta informações de endereço (rua, bairro, cidade, estado) a partir de um CEP brasileiro de 8 dígitos"</span></p>
										</div>
									</div>
								</div>

								<div className="flex items-start gap-2">
									<span className="text-amber-400 mt-0.5">2.</span>
									<div>
										<p className="text-textPrimary font-medium">Descreva os parâmetros</p>
										<p className="text-xs text-grayText">Inclua formato esperado e exemplos na descrição do parâmetro para a IA preencher corretamente.</p>
										<div className="mt-1 text-xs">
											<p><span className="text-red-400">✗</span> <span className="text-grayText">"cep"</span></p>
											<p><span className="text-green-400">✓</span> <span className="text-amber-300">"CEP com 8 dígitos, apenas números (ex: 01310100)"</span></p>
										</div>
									</div>
								</div>

								<div className="flex items-start gap-2">
									<span className="text-amber-400 mt-0.5">3.</span>
									<div>
										<p className="text-textPrimary font-medium">Teste antes de usar</p>
										<p className="text-xs text-grayText">Use o botão de teste no gerenciador de funções para verificar se a API está respondendo corretamente.</p>
									</div>
								</div>

								<div className="flex items-start gap-2">
									<span className="text-amber-400 mt-0.5">4.</span>
									<div>
										<p className="text-textPrimary font-medium">Nomes em snake_case</p>
										<p className="text-xs text-grayText">Use nomes como <code className="text-amber-300">consultar_cep</code>, <code className="text-amber-300">buscar_produto</code>. Serão convertidos automaticamente.</p>
									</div>
								</div>

								<div className="flex items-start gap-2">
									<span className="text-amber-400 mt-0.5">5.</span>
									<div>
										<p className="text-textPrimary font-medium">Instrua a IA no prompt</p>
										<p className="text-xs text-grayText">Mencione as funções disponíveis no seu prompt para a IA saber quando usá-las.</p>
										<div className="mt-1 text-xs">
											<p className="text-amber-300">"Quando o cliente perguntar sobre endereço, use a função consultar_cep para buscar as informações."</p>
										</div>
									</div>
								</div>

								<div className="flex items-start gap-2">
									<span className="text-amber-400 mt-0.5">6.</span>
									<div>
										<p className="text-textPrimary font-medium">Use APIs que retornam JSON</p>
										<p className="text-xs text-grayText">A IA funciona melhor com APIs que retornam dados em JSON. APIs que retornam HTML ou XML podem não funcionar bem.</p>
									</div>
								</div>
							</div>
						</section>

						{/* ─── FAQ ─── */}
						<section id="help-section-faq">
							<h3 className="text-base font-semibold text-amber-400 mb-3 flex items-center gap-2">
								<AlertTriangle className="w-4 h-4" />
								Perguntas Frequentes
							</h3>

							<AccordionItem title="Quantas funções posso cadastrar?" defaultOpen>
								<p>Não há limite fixo, mas recomendamos no máximo <strong className="text-amber-300">10-15 funções</strong> por instância. Muitas funções podem confundir a IA na hora de decidir qual usar.</p>
							</AccordionItem>

							<AccordionItem title="Posso usar APIs que exigem login/pagamento?">
								<p>Sim! Configure os headers de autenticação (Bearer Token, API Key, etc.) na seção de Headers da função. Cada requisição enviará esses headers automaticamente.</p>
							</AccordionItem>

							<AccordionItem title="A IA sempre chama a função corretamente?">
								<p>A IA decide quando e como chamar com base na <strong>descrição da função</strong> e na <strong>descrição dos parâmetros</strong>. Quanto mais claras forem, melhor a IA acerta. Também ajuda mencionar as funções no prompt do sistema.</p>
							</AccordionItem>

							<AccordionItem title="Posso enviar JSON aninhado (objeto dentro de objeto) no Body?">
								<p>Atualmente o Body é montado como um objeto <strong className="text-amber-300">plano</strong> (flat). Cada parâmetro vira uma chave no nível raiz do JSON. Estruturas aninhadas (ex: <code className="text-amber-300">{`{"contact": {"email": "..."}}`}</code>) não são suportadas diretamente.</p>
							</AccordionItem>

							<AccordionItem title="Funciona com Base de Conhecimento ativa?">
								<p><strong className="text-red-400">Não.</strong> As Ferramentas de API ficam desabilitadas quando a Base de Conhecimento está ativa. É uma limitação da API — você precisa escolher um ou outro.</p>
							</AccordionItem>

							<AccordionItem title="A API pode demorar para responder?">
								<p>O sistema tem um timeout de segurança. Se a API não responder em tempo hábil, a IA informará ao cliente que não conseguiu obter os dados. Prefira APIs com tempo de resposta rápido (&lt; 5 segundos).</p>
							</AccordionItem>

							<AccordionItem title="Posso usar DELETE e PUT?">
								<p><strong className="text-amber-300">PUT</strong> é suportado (funciona igual ao POST, com Body). <strong className="text-amber-300">DELETE</strong> é suportado mas sem Body (apenas Path e Query params). <strong className="text-amber-300">GET</strong> e <strong className="text-amber-300">POST</strong> são os mais usados.</p>
							</AccordionItem>
						</section>

						{/* ─── APIs GRATUITAS ─── */}
						<section className="pb-4">
							<h3 className="text-base font-semibold text-amber-400 mb-2 flex items-center gap-2">
								<Globe className="w-4 h-4" />
								APIs Gratuitas para Testar
							</h3>
							<p className="text-sm text-textSecondary mb-3">
								Use os exemplos prontos no gerenciador de funções, ou cadastre estas APIs públicas:
							</p>
							<div className="space-y-2 text-xs">
								{[
									{ name: "ViaCEP", url: "viacep.com.br", desc: "Consulta de CEP" },
									{ name: "BrasilAPI", url: "brasilapi.com.br", desc: "CEP, CNPJ, bancos, DDD, feriados" },
									{ name: "AwesomeAPI", url: "economia.awesomeapi.com.br", desc: "Cotação de moedas" },
									{ name: "CPTEC/INPE", url: "brasilapi.com.br/api/cptec", desc: "Previsão do tempo" },
								].map(api => (
									<div key={api.name} className="flex items-center gap-3 p-2 bg-black/20 rounded-lg border border-amber-500/15">
										<Globe className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
										<div>
											<span className="text-amber-300 font-medium">{api.name}</span>
											<span className="text-grayText ml-2">— {api.desc}</span>
										</div>
									</div>
								))}
							</div>
						</section>

					</div>
				</div>

				{/* Close button */}
				<div className="pt-3 border-t border-amber-500/15 flex justify-end">
					<button
						onClick={onClose}
						className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30 transition-colors text-sm font-medium"
					>
						Fechar
					</button>
				</div>
			</DialogContent>
		</Dialog>
	);
};
